import React from 'react';
import { Box, Text } from '@stitch-fix/mode-react';
import { type CloudinaryImageFields } from '../CloudinaryImageWrapper';
import { ShoppableItem, type ShoppableItemProps } from './ShoppableItem';
import styles from './styles.module.scss';
import { MODE_MIN_WIDTH } from '../../utils/values/mediaQueries';
type ShoppableItem = Omit<ShoppableItemProps, 'image'> & {
  image: CloudinaryImageFields;
};
export type ShoppableEditorialProps = {
  title: string;
  description?: string;
  coverItem: ShoppableItem;
  items: ShoppableItem[];
};
const dimensions = {
  coverItemWidth: 644,
  itemWidth: 308,
  containerGutter: 32,
  productGridGutter: 16,
  productDetailHeight: 24,
  // from mode-style-system
  productDetailGutter: 12
};
const getMainImageAspectRatio = (item: ShoppableItem) => {
  let aspectRatio = {
    width: 4,
    height: 5
  };
  const productDetailsCount = [item.name, item.brand, item.price].filter(Boolean).length;
  if (productDetailsCount > 0) {
    const productDetailsHeight = productDetailsCount * dimensions.productDetailHeight + dimensions.productDetailGutter;
    aspectRatio = {
      width: dimensions.coverItemWidth,
      height: dimensions.coverItemWidth * (aspectRatio.height / aspectRatio.width) + productDetailsHeight
    };
  }
  return `${aspectRatio.width} / ${aspectRatio.height}`;
};
const getProductAspectRatio = (item: ShoppableItem) => {
  let aspectRatio = {
    width: 1,
    height: 1
  };
  const productDetailsCount = [item.name, item.brand, item.price].filter(Boolean).length;
  if (productDetailsCount > 0) {
    const productDetailsHeight = productDetailsCount * dimensions.productDetailHeight + dimensions.productDetailGutter;
    aspectRatio = {
      width: dimensions.itemWidth * 2 + dimensions.productDetailGutter,
      height: (dimensions.itemWidth + productDetailsHeight) * 2
    };
  }
  return `${aspectRatio.width} / ${aspectRatio.height}`;
};
export const ShoppableEditorial = ({
  title,
  description,
  coverItem,
  items
}: ShoppableEditorialProps) => {
  const coverItemAspectRatio = getMainImageAspectRatio(coverItem);
  const productGridAspectRatio = getProductAspectRatio(items[0]);
  return <Box className={styles.container} mx="auto" px={{
    sm: 1,
    lg: 1.5
  }}>
      <Text setting="title-large" mb={0.5}>
        {title}
      </Text>
      {description && <Box maxWidth={47.25}>
          <Text setting="body-small" spacingBottom>
            {description}
          </Text>
        </Box>}
      <div data-testid="products-container" className={styles.productsContainer} style={{
      // @ts-expect-error css variables should be valid css properties
      '--main-item-aspect-ratio': coverItemAspectRatio,
      '--product-grid-aspect-ratio': productGridAspectRatio
    }}>
        <Box className={styles.coverItem}>
          <ShoppableItem brand={coverItem.brand} href={coverItem.href} name={coverItem.name} price={coverItem.price} image={{
          ...coverItem.image,
          aspectRatio: undefined,
          sources: {
            sm: dimensions.coverItemWidth
          },
          maxContainerWidth: 1356,
          sizes: [{
            minWidth: MODE_MIN_WIDTH.sm,
            fluidWidth: 100
          }, {
            minWidth: 644,
            staticWidth: 644
          }, {
            minWidth: MODE_MIN_WIDTH.lg,
            fluidWidth: 50
          }],
          imageProps: {
            loading: 'lazy'
          }
        }} />
        </Box>
        <Box className={styles.productsGrid}>
          {items.map(item => <ShoppableItem key={item.image.alt} brand={item.brand} href={item.href} name={item.name} price={item.price} image={{
          ...item.image,
          sources: {
            sm: dimensions.itemWidth
          },
          maxContainerWidth: 1356,
          sizes: [{
            minWidth: MODE_MIN_WIDTH.sm,
            fluidWidth: 50
          }, {
            minWidth: 644,
            staticWidth: 644 / 2
          }, {
            minWidth: MODE_MIN_WIDTH.lg,
            fluidWidth: 25
          }],
          imageProps: {
            loading: 'lazy'
          }
        }} />)}
        </Box>
      </div>
    </Box>;
};